"use client";
import * as React from "react";
import { useState, useCallback, useEffect } from "react";
import { usePathname, useSearchParams, useRouter } from "next/navigation";
import { NavOrganization } from "@/components/nav-organization";
import { NavPrimary } from "@/components/nav-primary";
import { NavSecondary } from "@/components/nav-secondary";
import { NavUser } from "@/components/nav-user";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
} from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { findGroupsByBaseUrl, findMainTitleByBaseUrl } from "@/utils/utils";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/useIsMobile";
import { EarlyAdopterDialog } from "@/components/glaci/early-adopter-dialog";
import { useAppNavigation } from "@/components/use-app-navigation";

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  const {
    data,
    organization,
    subscriptions,
    canReviewTax,
    hasPendingOnlyOrganization,
  } = useAppNavigation();
  const isMobile = useIsMobile();
  const [showEarlyAdopterDialog, setShowEarlyAdopterDialog] = useState(false);

  // Check for glaci=early-adopter query param (set when redirecting from Glaci routes)
  useEffect(() => {
    if (searchParams.get("glaci") === "early-adopter") {
      setShowEarlyAdopterDialog(true);
      // Clean up the URL by removing the query param
      router.replace(pathname, { scroll: false });
    }
  }, [searchParams, pathname, router]);

  const handleNavItemClick = useCallback(
    (item: { title: string; url: string }) => {
      // Intercept Glaci clicks when feature is not enabled
      if (item.title === "Glaci" && !organization?.isGlaciEnabled) {
        setShowEarlyAdopterDialog(true);
        return true; // Indicates click was handled
      }
      return false;
    },
    [organization?.isGlaciEnabled],
  );

  return (
    <Sidebar
      collapsible="icon"
      variant="inset"
      className="overflow-hidden [&>[data-sidebar=sidebar]]:flex-row"
      {...props}
    >
      {/* Primary sidebar */}
      <Sidebar
        collapsible="none"
        className={cn(
          "w-12 shrink-0 border-r md:!w-[calc(var(--sidebar-width-icon)_+_1px)]",
        )}
      >
        <SidebarHeader>
          <NavOrganization />
        </SidebarHeader>
        <SidebarContent className="lb-scrollbar-overlay pt-2">
          <NavPrimary items={data.navMain} onItemClick={handleNavItemClick} />
          <Separator className="mx-auto w-8" />
          <NavPrimary
            items={[
              ...data.navSecondary.filter((item) => item.url === "/insights"),
              ...data.navResources,
            ]}
          />
          <NavPrimary
            items={data.navSecondary.filter((i) => {
              if (i.url === "/insights") return false;
              if (i.title === "Upgrade to Plus" && hasPendingOnlyOrganization) {
                return false;
              }
              if (i.title === "Upgrade to Plus") {
                return !subscriptions.find(
                  (s) =>
                    s.productId ===
                      process.env.NEXT_PUBLIC_STRIPE_POWERON_PRODUCT_ID &&
                    s.status === "active",
                );
              }
              return true;
            })}
            className="mt-auto"
            showUpdateDialog={true}
          />
        </SidebarContent>
        <SidebarFooter>
          <NavUser />
        </SidebarFooter>
      </Sidebar>
      {/* Secondary sidebar, including the mobile drawer. */}
      <Sidebar
        collapsible="none"
        className={cn("flex-1", isMobile && "!w-[calc(100%-4.5rem)]")}
      >
        <SidebarHeader className="gap-3.5 border-b p-4">
          <div className="flex w-full items-center justify-between">
            <div className="text-base font-bold text-foreground">
              {findMainTitleByBaseUrl(data, pathname)}
            </div>
          </div>
        </SidebarHeader>
        <SidebarContent className="lb-scrollbar">
          <NavSecondary
            items={findGroupsByBaseUrl(data, pathname).filter(
              (group) => group.pages.length > 0,
            )}
          />
        </SidebarContent>
        {canReviewTax &&
          (pathname === "/insights" || pathname.startsWith("/insights/")) && (
            <SidebarFooter className="px-4 pb-4 pt-2">
              <p
                aria-label="Build information"
                className="flex select-text items-center justify-center gap-2 font-mono text-xs text-muted"
              >
                <span>
                  {process.env.NEXT_PUBLIC_BUILD_VERSION
                    ? `v${process.env.NEXT_PUBLIC_BUILD_VERSION}`
                    : "Unavailable"}
                </span>
                <span aria-hidden="true">·</span>
                <span title={process.env.NEXT_PUBLIC_COMMIT_HASH || undefined}>
                  {process.env.NEXT_PUBLIC_COMMIT_HASH?.slice(0, 7) ||
                    "Unavailable"}
                </span>
              </p>
            </SidebarFooter>
          )}
      </Sidebar>
      <EarlyAdopterDialog
        open={showEarlyAdopterDialog}
        onOpenChange={setShowEarlyAdopterDialog}
      />
    </Sidebar>
  );
}
